import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
} from "@mui/material";
import {
  createLessonsAPIEndpoint,
  EndPoints,
} from "../../../../services/LessonAPI";
import { useSnackbar } from "notistack";
import { CircularProgress } from "@mui/material";

function AddLessonDialog({ open, onClose, onLessonAdded, centers }) {
  const { enqueueSnackbar } = useSnackbar();

  const [lesson, setLesson] = useState({
    title: "",
    description: "",
    centerId: "",
    isFree: false,
    maxViewPerStudent: "",
  });

  const [videoFile, setVideoFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
 
  const handleChange = (field, value) => {
   setLesson({ ...lesson, [field]: value });
  };

  const validate = () => {
    let temp = {};
    temp.title = lesson.title ? "" : "Title is required.";
    temp.description = lesson.description ? "" : "Description is required.";
    temp.centerId = lesson.centerId
      ? ""
      : "Center is required.";
    temp.maxViewPerStudent =
      lesson.maxViewPerStudent && Number(lesson.maxViewPerStudent) > 0
        ? ""
        : "Max Views must be greater than 0.";
    temp.isFree = lesson.isFree !== undefined ? "" : "Please specify if the lesson is free.";

    setErrors(temp);
    return Object.values(temp).every((x) => x === "");
  };

  const handleAddLesson = async () => {
    if (!validate()) return;

    setSaving(true);
    try {
        const formData = new FormData();
        formData.append("title", lesson.title);
        formData.append("description", lesson.description);
        formData.append("centerId", lesson.centerId);
        formData.append("maxViewPerStudent", lesson.maxViewPerStudent);
        formData.append("isFree", lesson.isFree);
        if (videoFile) {
          formData.append("videoFile", videoFile);
        }

        const res = await createLessonsAPIEndpoint(EndPoints.addLesson)
          .postWithToken(formData, {
            headers: { "Content-Type": "multipart/form-data" },
          })
          const lessonId = res.data.lessonId;
          
        
          if (onLessonAdded) onLessonAdded(res.data);
            if (videoFile) {
              handleUploadVideo(lessonId, videoFile)
            }
            enqueueSnackbar("Lesson created successfully!", { variant: "success" });
            handleClose();
          
      } catch (err) {
        console.error("Failed to create lesson:", err);
        enqueueSnackbar(`Failed to create lesson: ${err.message}`, { variant: "error" });
      }
      finally{
        setSaving(false)
      };
  };


  const handleUploadVideo = async (lessonId, file) => {
    if (!file || !lessonId) return;
    const formData = new FormData();
    formData.append("videoFile", file);

    try {
    const res = await createLessonsAPIEndpoint(EndPoints.uploadVideo)
      .postById(lessonId, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
        if (onLessonAdded) onLessonAdded(res.data);
        enqueueSnackbar("Video Uploaded Successfully.", {
          variant: "success",
          style: { fontSize: "16px" },
        });
      } catch (err)  {
        console.error("Failed to upload video:", err);
        enqueueSnackbar(
          err.response.data.message || "Failed to upload video.",
          {
            variant: "error",
            style: { fontSize: "16px" },
          },
        );
      throw err;
    }
  };

  const handleClose = () => {
    setLesson({
      title: "",
      description: "",
      centerId: "",
      maxViewPerStudent: "",
      isFree: false,
    });
    setVideoFile(null);
    setErrors({});
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className="add-lesson-dialog"
    >
      <DialogTitle className="title">Add New Lesson</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Title"
          margin="normal"
          className="lesson-title-field"
          value={lesson.title}
          onChange={(e) => handleChange("title", e.target.value)}
          error={!!errors.title}
          helperText={errors.title}
        />
        <TextField
          fullWidth
          label="Description"
          margin="normal"
          className="lesson-title-field"
          value={lesson.description}
          onChange={(e) => handleChange("description", e.target.value)}
          error={!!errors.description}
          helperText={errors.description}
        />
        <TextField
          fullWidth
          select
          label="Center"
          margin="normal"
          className="lesson-title-field"
          value={lesson.centerId}
          onChange={(e) => handleChange("centerId", e.target.value)}
          error={!!errors.centerId}
          helperText={errors.centerId}
        >
          {centers.map((center) => (
            <MenuItem key={center.id} value={center.id} className="li">
              {center.name}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          type="number"
          label="Max Views Per Student"
          margin="normal"
          className="lesson-title-field"
          value={lesson.maxViewPerStudent}
          onChange={(e) => handleChange("maxViewPerStudent", e.target.value)}
          error={!!errors.maxViewPerStudent}
          helperText={errors.maxViewPerStudent}
        />
        <TextField
          fullWidth
          select
          label="Is Free"
          margin="normal"
          className="lesson-title-field"
          value={lesson.isFree}
          onChange={(e) => handleChange("isFree", e.target.value === "true")}
          error={!!errors.isFree}
          helperText={errors.isFree}
        >
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </TextField>


        {/* Upload Video */}
        <Button
          className="btn-upload-video"
          variant="contained"
          component="label"
          sx={{ mt: 2 }}
        >
          {videoFile ? "Change Video" : "Upload Video"}
          <input
            type="file"
            accept="video/*"
            hidden
            onChange={(e) => setVideoFile(e.target.files[0])}
          />
        </Button>
        {videoFile && (
          <p style={{ fontSize: "0.9rem", marginTop: "8px" }}>
            Selected: {videoFile.name}
          </p>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} className="btn-cancel">
          Cancel
        </Button>
        <Button
          onClick={handleAddLesson}
          variant="contained"
          className="btn-save"
          disabled={saving}
        >
          {saving ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddLessonDialog;
